import { useEffect, useState } from 'react';
import {
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  InputAdornment,
  TablePagination,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { createProbabilidad, deleteProbabilidad, getProbabilidad, updateProbabilidad } from '../../services/probabilidad/probabilidadData';
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";

const FRECUENCIAS = ["Diario", "Semanal", "Mensual", "Anual"];
const PROBABILIDAD = ["Muy Baja", "Baja", "Media", "Alta", "Muy Alta"];
const RANGO_MIN = 0;
const RANGO_MAX = 100;

const TablaPB = () => {
  const [datos, setDatos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modificarIndex, setModificarIndex] = useState(-1);
  const [rangoSlider, setRangoSlider] = useState([0, 0]);
  const { user } = useAuth();
  const emp_codigo = user?.emp_codigo;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (emp_codigo) {
          const probabilidadData = await getProbabilidad(emp_codigo);
          setDatos(Array.isArray(probabilidadData) ? probabilidadData : []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setDatos([]);
      }
    };

    fetchData();
  }, [emp_codigo]);

  const [formulario, setFormulario] = useState({
    ame_codigo: '',
    pro_descripcion: '',
    pro_probabilidad: '',
    pro_rangos: '',
    pro_valor: '',
    pro_frecuencia: '',
  });

  const handleBusquedaChange = (event) => {
    setBusqueda(event.target.value);
    setPage(0);
  };

  const handleInsert = () => {
    setModificarIndex(-1);
    setFormulario({
      ame_codigo: '',
      pro_descripcion: '',
      pro_probabilidad: '',
      pro_rangos: '',
      pro_valor: '',
      pro_frecuencia: '',
    });
    setRangoSlider([0, 0]);
    setModalOpen(true);
  };

  const handleFormChange = (event) => {
    setFormulario({
      ...formulario,
      [event.target.name]: event.target.value,
    });
  };

  const handleRangoChange = (event, newValue) => {
    setRangoSlider(newValue);
    setFormulario({
      ...formulario,
      pro_rangos: `${newValue[0]}-${newValue[1]}%`,
    });
  };

  const handleGuardar = async () => {
    const camposFaltantes = [];
    if (!formulario.pro_probabilidad) camposFaltantes.push("Probabilidad");
    if (!formulario.pro_rangos) camposFaltantes.push("Rango");
    if (!formulario.pro_valor) camposFaltantes.push("Valor");
    if (!formulario.pro_frecuencia) camposFaltantes.push("Frecuencia");

    if (camposFaltantes.length > 0) {
      toast.warning(`Faltan campos obligatorios: ${camposFaltantes.join(", ")}`);
      return;
    }
    const dataToSend = {
      emp_codigo: emp_codigo,
      pro_probabilidad: formulario.pro_probabilidad,
      pro_rangos: formulario.pro_rangos,
      pro_valor: formulario.pro_valor,
      pro_frecuencia: formulario.pro_frecuencia,
    };
    try {
      if (modificarIndex !== -1) {
        await updateProbabilidad(modificarIndex, dataToSend);
        const probabilidadData = await getProbabilidad(emp_codigo);
        setDatos(Array.isArray(probabilidadData) ? probabilidadData : []);
        toast.success("Probabilidad modificada correctamente");
      } else {
        await createProbabilidad(dataToSend);
        const probabilidadData = await getProbabilidad(emp_codigo);
        setDatos(Array.isArray(probabilidadData) ? probabilidadData : []);
        toast.success("Probabilidad creada correctamente");
      }
      setModalOpen(false);
      setFormulario({
        ame_codigo: '',
        pro_descripcion: '',
        pro_probabilidad: '',
        pro_rangos: '',
        pro_valor: '',
        pro_frecuencia: '',
      });
    } catch (error) {
      console.error("Error saving probability:", error);
      toast.error("Error al guardar la probabilidad");
    }
  };

  const handleCancelar = () => {
    setModalOpen(false);
    setFormulario({
      ame_codigo: '',
      pro_descripcion: '',
      pro_probabilidad: '',
      pro_rangos: '',
      pro_valor: '',
      pro_frecuencia: '',
    });
  };

  const handleModificar = (index) => {
    const probabilidad = datos[index];
    let min = 0, max = 0;
    if (probabilidad.pro_rangos) {
      const match = probabilidad.pro_rangos.match(/(\d+)-(\d+)%/);
      if (match) {
        min = parseInt(match[1], 10);
        max = parseInt(match[2], 10);
      }
    }
    setRangoSlider([min, max]);
    setModificarIndex(probabilidad.pro_codigo);
    setFormulario({
      pro_codigo: probabilidad.pro_codigo,
      pro_descripcion: probabilidad.pro_descripcion,
      pro_probabilidad: probabilidad.pro_probabilidad,
      pro_rangos: probabilidad.pro_rangos,
      pro_valor: probabilidad.pro_valor,
      pro_frecuencia: probabilidad.pro_frecuencia,
    });
    setModalOpen(true);
  };

  const handleEliminar = (index) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const idToDelete = datos[deleteIndex].pro_codigo;
      await deleteProbabilidad(idToDelete);
      toast.success("Probabilidad eliminada correctamente");

      const probabilidadData = await getProbabilidad(emp_codigo);
      setDatos(Array.isArray(probabilidadData) ? probabilidadData : []);
      setConfirmOpen(false);
      setDeleteIndex(null);
    } catch (error) {
      console.error("Error deleting probability:", error);
      const mensaje =
        error?.message ||
        error?.response?.data?.message ||
        "No se puede eliminar la probabilidad porque está siendo utilizada en el sistema.";
      if (
        error?.status === 400 ||
        mensaje.toLowerCase().includes("no se puede eliminar") ||
        mensaje.toLowerCase().includes("asociada") ||
        error?.response?.data?.errno === 1451
      ) {
        setErrorMessage(mensaje);
        setErrorModalOpen(true);
      } else {
        toast.error(mensaje);
      }
      setConfirmOpen(false);
      setDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const datosFiltrados = datos.filter((dato) => {
    const busq = busqueda.toLowerCase();
    return (
      dato.pro_probabilidad?.toString().toLowerCase().includes(busq) ||
      dato.pro_valor?.toString().toLowerCase().includes(busq)
    );
  });

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar por probabilidad o valor..."
          value={busqueda}
          onChange={handleBusquedaChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleInsert}
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            '&:hover': { backgroundColor: '#003366' },
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Nueva Probabilidad
        </Button>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Probabilidad</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Rango</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Valor</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Frecuencia</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    {datos.length === 0 ? "No hay probabilidades registradas." : "No se encontraron resultados."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              datosFiltrados
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato) => (
                  <TableRow key={dato.pro_codigo} hover>
                    <TableCell>{dato.pro_probabilidad}</TableCell>
                    <TableCell>{dato.pro_rangos}</TableCell>
                    <TableCell>{dato.pro_valor}</TableCell>
                    <TableCell>{dato.pro_frecuencia}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <IconButton onClick={() => handleModificar(datos.findIndex(d => d.pro_codigo === dato.pro_codigo))} size="small" sx={{ color: "#666" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            const originalIndex = datos.findIndex(d => d.pro_codigo === dato.pro_codigo);
                            setDeleteIndex(originalIndex);
                            setConfirmOpen(true);
                          }}
                          size="small"
                          sx={{ color: "#d32f2f" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={datosFiltrados.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
      />

      {/* Modal Formulario */}
      <Dialog
        open={modalOpen}
        onClose={handleCancelar}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          {modificarIndex !== -1 ? 'Modificar Probabilidad' : 'Nueva Probabilidad'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              label="Probabilidad"
              name="pro_probabilidad"
              fullWidth
              value={formulario.pro_probabilidad}
              onChange={handleFormChange}
              sx={{ mb: 3 }}
              variant="outlined"
            >
              {PROBABILIDAD.map((prob) => (
                <MenuItem key={prob} value={prob}>
                  {prob}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ mb: 3, px: 1 }}>
              <Typography gutterBottom variant="subtitle2" color="textSecondary">
                Rango ({rangoSlider[0]}-{rangoSlider[1]}%)
              </Typography>
              <Slider
                value={rangoSlider}
                onChange={handleRangoChange}
                valueLabelDisplay="auto"
                min={RANGO_MIN}
                max={RANGO_MAX}
                step={1}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 25, label: '25%' },
                  { value: 50, label: '50%' },
                  { value: 75, label: '75%' },
                  { value: 100, label: '100%' },
                ]}
                disableSwap
                sx={{ color: '#3A6B7E' }}
              />
            </Box>
            <TextField
              label="Valor"
              name="pro_valor"
              fullWidth
              value={formulario.pro_valor}
              onChange={handleFormChange}
              sx={{ mb: 3 }}
              variant="outlined"
            />
            <TextField
              select
              label="Frecuencia"
              name="pro_frecuencia"
              fullWidth
              value={formulario.pro_frecuencia}
              onChange={handleFormChange}
              variant="outlined"
            >
              {FRECUENCIAS.map((freq) => (
                <MenuItem key={freq} value={freq}>
                  {freq}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelar} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleGuardar}
            variant="contained"
            sx={{
              backgroundColor: '#001F3F',
              color: '#fff',
              '&:hover': { backgroundColor: '#003366' }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmación Eliminación */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666' }}>
            ¿Está seguro que desea eliminar esta probabilidad?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDelete} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              '&:hover': { backgroundColor: '#c62828' }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de error */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          No se puede eliminar
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
              {errorMessage || 'Esta probabilidad está siendo utilizada y no puede eliminarse.'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Antes de eliminarla, actualiza o elimina las relaciones donde se usa.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setErrorModalOpen(false)}
            variant="contained"
            sx={{ backgroundColor: '#001F3F', color: '#fff', '&:hover': { backgroundColor: '#003366' } }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablaPB;
